import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useAxiosWrapper } from '../../services/axiosWrapper';
import { authAtom } from '../usuarios/AuthState';
import { history } from '../../helpers/history';
import {
  curriculoAnalysisAtom,
  curriculoSuggestionsAtom,
  curriculoLoadingAtom,
  curriculoProcessingAtom,
  CurriculoAnalysisResult,
  SugestoesMelhoriaResponse,
  ImportarCurriculoRequest,
  SugestoesMelhoriaSecaoRequest,
  SugestoesMelhoriaCampoRequest,
  CurriculoHistoryItem,
  SaveHistoryRequest,
} from './CurriculoState';

function useCurriculoActions() {
  const axiosWrapper = useAxiosWrapper();
  const authState = useRecoilValue(authAtom);
  const setAuth = useSetRecoilState(authAtom);

  // Setters para os atoms
  const setCurriculoAnalysis = useSetRecoilState(curriculoAnalysisAtom);
  const setCurriculoSuggestions = useSetRecoilState(curriculoSuggestionsAtom);
  const setCurriculoLoading = useSetRecoilState(curriculoLoadingAtom);
  const setCurriculoProcessing = useSetRecoilState(curriculoProcessingAtom);

  const baseUrl = `${process.env.REACT_APP_API_URL}/v1/curriculo`;

  return {
    importarCurriculo,
    obterSugestoesMelhoriaSecao,
    obterSugestoesMelhoriaCampo,
    limparAnalise,
    limparSugestoes,
    gerarCVAtualizado,
    salvarHistorico,
    listarHistorico
  };

  /**
   * Importa e processa um currículo com IA
   */
  async function importarCurriculo(request: ImportarCurriculoRequest): Promise<CurriculoAnalysisResult> {
    setCurriculoProcessing(true);
    setCurriculoAnalysis(null);

    try {
      const formData = new FormData();
      formData.append('cvFile', request.cvFile);

      const url = `${baseUrl}/importar-curriculo`;

      const response = await axiosWrapper.postFormData(url, formData);

      setCurriculoAnalysis(response);

      return response;
    } catch (error) {
      handleError(error);
      throw error;
    } finally {
      setCurriculoProcessing(false);
    }
  }

  async function obterSugestoesMelhoriaSecao(request: SugestoesMelhoriaSecaoRequest): Promise<SugestoesMelhoriaResponse> {
    setCurriculoLoading(true);
    setCurriculoSuggestions(null);

    try {
      const url = `${baseUrl}/sugestoes-melhoria/secao`;

      const requestData = {
        secao: request.secao,
        caminhoCampo: null,
        valorAtual: null,
        dadosCurriculo: request.dadosCurriculo,
        contatoId: request.contatoId
      };

      const response = await axiosWrapper.post(url, requestData);

      setCurriculoSuggestions(response);

      return response;
    } catch (error) {
      handleError(error);
      throw error;
    } finally {
      setCurriculoLoading(false);
    }
  }

  async function obterSugestoesMelhoriaCampo(request: SugestoesMelhoriaCampoRequest): Promise<SugestoesMelhoriaResponse> {
    setCurriculoLoading(true);
    setCurriculoSuggestions(null);

    try {
      const url = `${baseUrl}/sugestoes-melhoria/campo`;

      const requestData = {
        secao: request.secao,
        caminhoCampo: request.caminhoCampo,
        valorAtual: request.valorAtual,
        dadosCurriculo: request.dadosCurriculo,
        contatoId: request.contatoId
      };

      const response = await axiosWrapper.post(url, requestData);

      // Salva as sugestões no state
      setCurriculoSuggestions(response);

      return response;
    } catch (error) {
      handleError(error);
      throw error;
    } finally {
      setCurriculoLoading(false);
    }
  }

  async function gerarCVAtualizado(request: SugestoesMelhoriaResponse) {
    setCurriculoLoading(true);

    try {
      const url = `${baseUrl}/gerar-cv`;

      const response = await axiosWrapper.post(url, request);
      return response;
    } catch (error) {
      handleError(error);
      throw error;
    } finally {
      setCurriculoLoading(false);
    }
  }


  async function salvarHistorico(request: SaveHistoryRequest): Promise<CurriculoHistoryItem> {
    return axiosWrapper.post(`${baseUrl}/salvar-historico`, request);
  }

  async function listarHistorico(contatoId: number): Promise<CurriculoHistoryItem[]> {
    return axiosWrapper.get(`${baseUrl}/historico?contatoId=${contatoId}`);
  }

  /**
   * Limpa os dados de análise do currículo
   */
  function limparAnalise() {
    setCurriculoAnalysis(null);
    setCurriculoProcessing(false);
  }

  /**
   * Limpa as sugestões de melhoria
   */
  function limparSugestoes() {
    setCurriculoSuggestions(null);
    setCurriculoLoading(false);
  }

  /**
   * Trata erros comuns das requisições
   */
  function handleError(error: any) {
    if (error === 'Usuário inválido') {
      localStorage.removeItem('usuario');
      setAuth(null);
      history.push('/login');
    }
  }
}

export { useCurriculoActions };
